import type {
  PrescriptionRuleResponse,
  PrescriptionRulePeriodResponse
} from 'generated/index';

import type { PrescriptionRuleCellValue } from './PrescriptionRuleCell';

import type { RosterDay } from 'components/shared/RosterViewDataGrid';
import { getWeekFieldName } from 'components/shared/RosterViewDataGrid/hooks/useRosterTimelineColumns/utils';

export const ROW_HEIGHT = 44;

// TEMPORARY. We'll add period selection later
export const MAX_WEEKS = 8;

const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

type WeekData = Record<string, string | PrescriptionRuleCellValue | null>;

const getWeekBounds = (week: RosterDay[]) => ({
  start: week[0].date,
  end: week[week.length - 1].date
});

const periodOverlapsWeek = (
  period: PrescriptionRulePeriodResponse,
  weekStart: Date,
  weekEnd: Date,
  fallbackEnd?: Date
): boolean => {
  const periodStart = new Date(period.start);
  if (!period.end) {
    if (fallbackEnd) {
      return periodStart <= weekEnd && fallbackEnd >= weekStart;
    }
    return periodStart <= weekEnd;
  }
  const periodEnd = new Date(period.end);
  return periodStart <= weekEnd && periodEnd >= weekStart;
};

const findOverlappingWeekIndices = (
  period: PrescriptionRulePeriodResponse,
  weeks: RosterDay[][],
  fallbackEnd?: Date
): number[] =>
  weeks.reduce<number[]>((acc, week, i) => {
    const { start, end } = getWeekBounds(week);
    if (periodOverlapsWeek(period, start, end, fallbackEnd)) acc.push(i);
    return acc;
  }, []);

const applyTotalPeriod = (
  weekData: WeekData,
  period: PrescriptionRulePeriodResponse,
  weeks: RosterDay[][],
  weekFields: string[],
  fallbackEnd?: Date
): void => {
  const overlappingIndices = findOverlappingWeekIndices(
    period,
    weeks,
    fallbackEnd
  );
  if (!overlappingIndices.length) return;

  const firstIdx = overlappingIndices[0];
  const lastIdx = overlappingIndices[overlappingIndices.length - 1];
  const spanLength = lastIdx - firstIdx + 1;

  const effectiveEnd = period.end ? new Date(period.end) : fallbackEnd;

  const totalWeeks = effectiveEnd
    ? Math.round(
        (effectiveEnd.getTime() - new Date(period.start).getTime()) / WEEK_IN_MS
      )
    : undefined;

  weekData[weekFields[firstIdx]] = {
    frequency: period.frequency,
    cellColSpan: spanLength,
    totalWeeks
  };

  // Null out spanned cells so they don't render independently
  for (let i = firstIdx + 1; i <= lastIdx; i++) {
    weekData[weekFields[i]] = null;
  }
};

const applyRecurringPeriod = (
  weekData: WeekData,
  period: PrescriptionRulePeriodResponse,
  weeks: RosterDay[][],
  weekFields: string[],
  fallbackEnd?: Date
): void => {
  weeks.forEach((week, i) => {
    const { start, end } = getWeekBounds(week);
    if (!periodOverlapsWeek(period, start, end, fallbackEnd)) return;

    const existing = weekData[weekFields[i]];
    const prevFrequency =
      existing && typeof existing === 'object' ? existing.frequency : 0;

    weekData[weekFields[i]] = {
      frequency: prevFrequency + period.frequency,
      cellColSpan: 1
    };
  });
};

export const buildWeekDataForRule = (
  rule: PrescriptionRuleResponse,
  weeks: RosterDay[][],
  prescriptionEndDate?: string
): WeekData => {
  const weekFields = weeks.map((week) =>
    getWeekFieldName(week[0].weekNr, week[0].date.getFullYear())
  );

  const weekData: WeekData = Object.fromEntries(
    weekFields.map((field) => [field, null])
  );

  const fallbackEnd = prescriptionEndDate
    ? new Date(prescriptionEndDate)
    : undefined;

  for (const period of rule.prescriptionRulePeriods) {
    const periodFallbackEnd = !period.end ? fallbackEnd : undefined;
    if (period.recurrenceType === 'TOTAL') {
      applyTotalPeriod(weekData, period, weeks, weekFields, periodFallbackEnd);
    } else {
      applyRecurringPeriod(
        weekData,
        period,
        weeks,
        weekFields,
        periodFallbackEnd
      );
    }
  }

  return weekData;
};
